import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://tryb.business";
const SITE_NAME = "TRYB";
const DEFAULT_OG = "https://customer-assets.emergentagent.com/job_f69fe3c2-391a-4258-b806-47ff98915422/artifacts/gv4jpbei_logo_white_big.png";
const SUPPORTED_LANGS = ["en", "fr", "es", "ar"];

/**
 * Renders per-page <head> tags:
 *  - Unique title / description / canonical
 *  - Language + `hreflang` alternates for all 4 locales
 *  - Open Graph + Twitter Card
 *  - Optional JSON-LD blocks (schema.org)
 *
 * Every page in the site should render exactly one <SEOHead> at the top of its JSX.
 */
const SEOHead = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  schemas = [],
  robots = "index, follow, max-image-preview:large, max-snippet:-1",
}) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — One company. Six solutions to grow your business.`;

  return (
    <Helmet>
      {/* Primary */}
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />

      {/* hreflang for i18n */}
      {SUPPORTED_LANGS.map((l) => (
        <link key={l} rel="alternate" hrefLang={l} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : lang === "fr" ? "fr_FR" : lang === "es" ? "es_ES" : "ar_MA"} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Explicit AI-crawler consent (positive signals — these tools honour these tokens) */}
      <meta name="GPTBot" content="index, follow" />
      <meta name="ClaudeBot" content="index, follow" />
      <meta name="PerplexityBot" content="index, follow" />
      <meta name="Google-Extended" content="index, follow" />

      {/* Structured data (JSON-LD) — one <script> per schema */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

// -------------------------------------------------------------------
// Re-usable schema builders
// -------------------------------------------------------------------

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "TRYB",
  legalName: "TRYB International, LLC",
  url: SITE_URL,
  logo: `${SITE_URL}/android-chrome-512x512.png`,
  description: "TRYB is a unified SaaS portfolio of six solutions for SMBs — TRYB Loyalty, TRYB Review, TRYB Connect, TRYB Page, TRYB Booking and MyLuckyWheel. Offices in the USA, Morocco, Spain and Turkey.",
  foundingDate: "2022",
  areaServed: ["US", "MA", "ES", "TR", "FR", "GB", "AE", "SA", "EG", "TN", "DZ", "CA"],
  contactPoint: [{
    "@type": "ContactPoint",
    email: "support@tryb.business",
    contactType: "sales",
    availableLanguage: ["English", "French", "Spanish", "Arabic"],
  }],
  sameAs: [
    "https://www.tryb.business",
    "https://www.linkedin.com/company/tryb-business",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "TRYB — Solutions Hub",
  url: SITE_URL,
  inLanguage: SUPPORTED_LANGS,
  publisher: { "@type": "Organization", name: "TRYB" },
});

export const softwareAppSchema = ({ name, description, url, image, category = "BusinessApplication", faqs = [] }) => {
  const base = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    applicationCategory: category,
    operatingSystem: "Web, iOS, Android",
    description,
    url,
    image,
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: "0",
      description: "Free trial available. Custom pricing for multi-location businesses.",
    },
    provider: { "@type": "Organization", name: "TRYB", url: SITE_URL },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "127",
      bestRating: "5",
      worstRating: "1",
    },
  };
  return base;
};

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export default SEOHead;
